import { End, Game } from '../types/curling';

/**
 * Baseball-style per-end scoring rates: Points For Average (PFA), Points Given
 * Average (PGA), Stolen Points For Average (SPFA), and Stolen Points Given
 * Average (SPGA) — "stolen" meaning scored in an end without hammer. Ends is
 * the denominator throughout (the curling analogue of a baseball game), not
 * games.
 */

/**
 * Which team holds hammer for a given end, independent of the live-game page's
 * own copy of this logic (LiveGame.tsx's getTeamWithHammer) — that version
 * additionally consults localStorage for an LSD result on end 1, which only
 * exists in the browser that ran the live-stone draw and isn't part of the
 * saved Game, so it can't be reproduced here for a stats pass over arbitrary
 * games. Recorded hammerOverride and startsWithHammer cover every case that
 * matters once a game has actually been played.
 */
export function hammerTeamForEnd(game: Game, endNumber: number): 'team1' | 'team2' {
  const end = game.ends.find(e => e.number === endNumber);
  if (end?.hammerOverride) return end.hammerOverride;

  if (endNumber === 1) {
    return game.startsWithHammer ?? 'team1';
  }

  const previousEnd = game.ends.find(e => e.number === endNumber - 1);
  if (!previousEnd) return game.startsWithHammer ?? 'team1';

  // Blank end: hammer normally stays put, except two-player (mixed doubles-
  // style) games where it switches — same rule LiveGame.tsx's own hammer
  // tracker uses.
  if (previousEnd.team1Score === 0 && previousEnd.team2Score === 0) {
    const previousHammer = hammerTeamForEnd(game, endNumber - 1);
    const isTwoPlayerCurling = game.team1Players.length === 2 && game.team2Players.length === 2;
    return isTwoPlayerCurling ? (previousHammer === 'team1' ? 'team2' : 'team1') : previousHammer;
  }

  // Whoever scored loses the hammer.
  if (previousEnd.team1Score > previousEnd.team2Score) return 'team2';
  if (previousEnd.team2Score > previousEnd.team1Score) return 'team1';

  return hammerTeamForEnd(game, endNumber - 1);
}

/**
 * Ends actually played — completed, or with at least one shot or a recorded
 * score — as opposed to game.ends.length, which is always pre-sized to the
 * number of ends chosen at game setup regardless of how far the game has
 * actually gotten.
 */
export function endsPlayedCount(game: Game): number {
  return game.ends.filter(
    (e: End) => e.completed || e.shots.length > 0 || e.team1Score > 0 || e.team2Score > 0
  ).length;
}

/**
 * The ends denominator PFA/PGA/SPFA/SPGA divide by for one game: the full
 * game-setup end count while the game is still in progress (so the figure
 * reads as a steady per-end pace rather than swinging end to end), and the
 * actual number of ends played once the game is complete.
 */
export function endsForAverage(game: Game): number {
  return game.isComplete ? endsPlayedCount(game) : game.ends.length;
}

export interface TeamPointsBreakdown {
  pointsFor: number;
  pointsGiven: number;
  stolenPointsFor: number;
  stolenPointsGiven: number;
}

const emptyBreakdown = (): TeamPointsBreakdown => ({
  pointsFor: 0, pointsGiven: 0, stolenPointsFor: 0, stolenPointsGiven: 0,
});

/** Raw (un-averaged) points-for/given and stolen-points-for/given for both sides of one game. */
export function computeGamePointsBreakdown(game: Game): { team1: TeamPointsBreakdown; team2: TeamPointsBreakdown } {
  const team1 = emptyBreakdown();
  const team2 = emptyBreakdown();

  game.ends.forEach(end => {
    // Unplayed and genuinely blank ends look identical (0-0) and contribute
    // nothing either way, so there's no need to tell them apart here.
    if (end.team1Score === 0 && end.team2Score === 0) return;

    const hammer = hammerTeamForEnd(game, end.number);

    if (end.team1Score > 0) {
      team1.pointsFor += end.team1Score;
      team2.pointsGiven += end.team1Score;
      if (hammer !== 'team1') {
        team1.stolenPointsFor += end.team1Score;
        team2.stolenPointsGiven += end.team1Score;
      }
    } else {
      team2.pointsFor += end.team2Score;
      team1.pointsGiven += end.team2Score;
      if (hammer !== 'team2') {
        team2.stolenPointsFor += end.team2Score;
        team1.stolenPointsGiven += end.team2Score;
      }
    }
  });

  return { team1, team2 };
}

export interface TeamPointsAverages extends TeamPointsBreakdown {
  /** Ends the averages below were divided by — see endsForAverage. */
  endsUsed: number;
  pfa: number | null;
  pga: number | null;
  spfa: number | null;
  spga: number | null;
}

const toAverages = (breakdown: TeamPointsBreakdown, ends: number): TeamPointsAverages => ({
  ...breakdown,
  endsUsed: ends,
  pfa: ends > 0 ? breakdown.pointsFor / ends : null,
  pga: ends > 0 ? breakdown.pointsGiven / ends : null,
  spfa: ends > 0 ? breakdown.stolenPointsFor / ends : null,
  spga: ends > 0 ? breakdown.stolenPointsGiven / ends : null,
});

/** PFA/PGA/SPFA/SPGA for both sides of one game — the BRC Scoring Teams page's own numbers. */
export function computeGamePointsAverages(game: Game): { team1: TeamPointsAverages; team2: TeamPointsAverages } {
  const { team1, team2 } = computeGamePointsBreakdown(game);
  const ends = endsForAverage(game);
  return { team1: toAverages(team1, ends), team2: toAverages(team2, ends) };
}

export interface TeamPointsStats extends TeamPointsBreakdown {
  /** Total ends actually played across every game counted — always the real
   *  count, never a game's configured setup total, unlike the single-game
   *  live view above. */
  endsPlayed: number;
  pfa: number | null;
  pga: number | null;
  spfa: number | null;
  spga: number | null;
}

/**
 * PFA/PGA/SPFA/SPGA rolled up across every game {@code teamName} has played —
 * the cumulative-reporting counterpart to computeGamePointsAverages, for
 * calculateTeamStats. Sums raw points/steals and actual ends played across
 * every game the team appears in (complete or still in progress, same games
 * calculateTeamStats' own totalPoints/totalPointsAgainst already include),
 * then divides once at the end.
 */
export function calculateTeamPointsStats(games: Game[], teamName: string): TeamPointsStats {
  const totals = emptyBreakdown();
  let endsPlayed = 0;

  games
    .filter(game => game.team1Name === teamName || game.team2Name === teamName)
    .forEach(game => {
      const { team1, team2 } = computeGamePointsBreakdown(game);
      const side = game.team1Name === teamName ? team1 : team2;
      totals.pointsFor += side.pointsFor;
      totals.pointsGiven += side.pointsGiven;
      totals.stolenPointsFor += side.stolenPointsFor;
      totals.stolenPointsGiven += side.stolenPointsGiven;
      endsPlayed += endsPlayedCount(game);
    });

  return {
    ...totals,
    endsPlayed,
    pfa: endsPlayed > 0 ? totals.pointsFor / endsPlayed : null,
    pga: endsPlayed > 0 ? totals.pointsGiven / endsPlayed : null,
    spfa: endsPlayed > 0 ? totals.stolenPointsFor / endsPlayed : null,
    spga: endsPlayed > 0 ? totals.stolenPointsGiven / endsPlayed : null,
  };
}

/**
 * Five more end-outcome rates, all as a percentage of some subset of ends
 * played:
 *
 *  - Hammer Conversion Rate: of the team's own hammer ends, how many did it
 *    turn into 2+ points (a real hammer advantage, not just a minimal hold).
 *  - Force %: of the ends where the OPPONENT had hammer, how many did this
 *    team hold the opponent to exactly 1 point on — the classic curling
 *    "force". Scored from the defending team's side, so team1's forcedEnds
 *    increments when team2 (with hammer) settles for 1, and vice versa.
 *  - Steal Rate: of the ends where the opponent had hammer, how many did this
 *    team score on anyway (any amount — SPFA is the points version of this,
 *    this is the end-count version).
 *  - Blank Rate: of every end played, how many were 0-0 — same figure for
 *    both teams in a given game, since a blank end is shared, but rolled up
 *    per team for cumulative reporting across different opponents.
 *  - Big-End Rate: of every end played, how many did this team score 3+ on
 *    (bigEndRateFor), and how many did the opponent score 3+ on against this
 *    team (bigEndRateAgainst).
 */
export interface TeamEndOutcomeCounts {
  playedEnds: number;
  hammerEnds: number;
  nonHammerEnds: number;
  hammerConversions: number;
  forcedEnds: number;
  stolenEnds: number;
  blankEnds: number;
  bigEndsFor: number;
  bigEndsAgainst: number;
}

const emptyEndOutcomeCounts = (): TeamEndOutcomeCounts => ({
  playedEnds: 0, hammerEnds: 0, nonHammerEnds: 0, hammerConversions: 0,
  forcedEnds: 0, stolenEnds: 0, blankEnds: 0, bigEndsFor: 0, bigEndsAgainst: 0,
});

// A hammer end scoring exactly this many (or fewer) is a force against the
// team that had hammer, rather than a real conversion.
const FORCE_MAX_POINTS = 1;
// Scoring at least this many in one end is a "big end".
const BIG_END_MIN_POINTS = 3;

/** Raw end-outcome counts for both sides of one game. */
export function computeGameEndOutcomeCounts(game: Game): { team1: TeamEndOutcomeCounts; team2: TeamEndOutcomeCounts } {
  const team1 = emptyEndOutcomeCounts();
  const team2 = emptyEndOutcomeCounts();

  game.ends.forEach(end => {
    const played = end.completed || end.shots.length > 0 || end.team1Score > 0 || end.team2Score > 0;
    if (!played) return;

    team1.playedEnds++;
    team2.playedEnds++;

    const hammer = hammerTeamForEnd(game, end.number);
    const hammerCounts = hammer === 'team1' ? team1 : team2;
    const nonHammerCounts = hammer === 'team1' ? team2 : team1;
    hammerCounts.hammerEnds++;
    nonHammerCounts.nonHammerEnds++;

    if (end.team1Score === 0 && end.team2Score === 0) {
      team1.blankEnds++;
      team2.blankEnds++;
      return;
    }

    const scorer: 'team1' | 'team2' = end.team1Score > 0 ? 'team1' : 'team2';
    const scorerPoints = scorer === 'team1' ? end.team1Score : end.team2Score;
    const scorerCounts = scorer === 'team1' ? team1 : team2;
    const otherCounts = scorer === 'team1' ? team2 : team1;

    if (scorer === hammer) {
      if (scorerPoints > FORCE_MAX_POINTS) {
        scorerCounts.hammerConversions++;
      } else {
        otherCounts.forcedEnds++;
      }
    } else {
      scorerCounts.stolenEnds++;
    }

    if (scorerPoints >= BIG_END_MIN_POINTS) {
      scorerCounts.bigEndsFor++;
      otherCounts.bigEndsAgainst++;
    }
  });

  return { team1, team2 };
}

export interface TeamEndOutcomeRates extends TeamEndOutcomeCounts {
  hammerConversionRate: number | null;
  forcePercent: number | null;
  stealRate: number | null;
  blankRate: number | null;
  bigEndRateFor: number | null;
  bigEndRateAgainst: number | null;
}

const pct = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? (numerator / denominator) * 100 : null;

const toEndOutcomeRates = (counts: TeamEndOutcomeCounts): TeamEndOutcomeRates => ({
  ...counts,
  hammerConversionRate: pct(counts.hammerConversions, counts.hammerEnds),
  forcePercent: pct(counts.forcedEnds, counts.nonHammerEnds),
  stealRate: pct(counts.stolenEnds, counts.nonHammerEnds),
  blankRate: pct(counts.blankEnds, counts.playedEnds),
  bigEndRateFor: pct(counts.bigEndsFor, counts.playedEnds),
  bigEndRateAgainst: pct(counts.bigEndsAgainst, counts.playedEnds),
});

/** Hammer Conversion Rate / Force % / Steal Rate / Blank Rate / Big-End Rate for one game. */
export function computeGameEndOutcomeRates(game: Game): { team1: TeamEndOutcomeRates; team2: TeamEndOutcomeRates } {
  const { team1, team2 } = computeGameEndOutcomeCounts(game);
  return { team1: toEndOutcomeRates(team1), team2: toEndOutcomeRates(team2) };
}

/** Same five rates, rolled up across every game teamName has played — the calculateTeamStats counterpart. */
export function calculateTeamEndOutcomeRates(games: Game[], teamName: string): TeamEndOutcomeRates {
  const totals = emptyEndOutcomeCounts();

  games
    .filter(game => game.team1Name === teamName || game.team2Name === teamName)
    .forEach(game => {
      const { team1, team2 } = computeGameEndOutcomeCounts(game);
      const side = game.team1Name === teamName ? team1 : team2;
      totals.playedEnds += side.playedEnds;
      totals.hammerEnds += side.hammerEnds;
      totals.nonHammerEnds += side.nonHammerEnds;
      totals.hammerConversions += side.hammerConversions;
      totals.forcedEnds += side.forcedEnds;
      totals.stolenEnds += side.stolenEnds;
      totals.blankEnds += side.blankEnds;
      totals.bigEndsFor += side.bigEndsFor;
      totals.bigEndsAgainst += side.bigEndsAgainst;
    });

  return toEndOutcomeRates(totals);
}
